import React from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { isEmpty } from 'lodash'
import { Controller, useForm } from 'react-hook-form'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import useAuth from 'hooks/useAuth'

import * as service from 'service'
import useStyles from './styles'
import helpers from 'helpers'

const GenerateReportDialog = ({ open, onClose }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const { user } = useAuth()

  const { control, handleSubmit, watch, setValue, reset, formState } = useForm({
    defaultValues: {
      reportType: 'complete',
      format: 'pdf',
      departments: [],
      emails: user?.email || '',
    },
  })

  const reportType = watch('reportType')
  const isSubmitting = formState.isSubmitting

  const { response: departmentsResponse, isLoading: isLoadingDepartments } =
    useFetch(service.dponet.trainingCourse.departments, {}, [], open)

  const departments = departmentsResponse?.data?.departments || []

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmit = async (data) => {
    const emails = data.emails
      .split(/[;,]/)
      .map((email) => email.trim())
      .filter((email) => !isEmpty(email))

    if (isEmpty(emails)) {
      snackbar.open({
        message: 'Informe pelo menos um e-mail para receber o relatório.',
        variant: 'error',
      })
      return
    }

    if (data.reportType === 'by_department' && isEmpty(departments)) {
      snackbar.open({
        message: 'Selecione pelo menos um departamento.',
        variant: 'error',
      })
      return
    }

    try {
      const response = await service.dponet.trainingCourse.generateReport({
        reportType: data.reportType,
        format: data.format,
        departmentIds:
          data.reportType === 'by_department'
            ? data?.departments.map((department) => department?.id)
            : [],
        emails,
      })

      snackbar.open({
        message:
          response?.data?.message ||
          'Relatório solicitado! Você receberá um e-mail em breve com o arquivo.',
        variant: 'info',
      })
      handleClose()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao solicitar o relatório. Tente novamente.',
        variant: 'error',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title}>Gerar Relatório</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.sectionBox}>
          <Typography className={classes.sectionLabel}>
            Escolha a versão do relatório:
          </Typography>
          <Controller
            as={
              <RadioGroup className={classes.radioGroup}>
                <FormControlLabel
                  value="complete"
                  control={<Radio color="primary" size="small" />}
                  label="Relatório Completo"
                  className={classes.formControlLabel}
                />
                <FormControlLabel
                  value="by_department"
                  control={<Radio color="primary" size="small" />}
                  label="Relatório por Departamento"
                  className={classes.formControlLabel}
                />
                <FormControlLabel
                  value="by_student"
                  control={<Radio color="primary" size="small" />}
                  label="Relatório por Colaborador"
                  className={classes.formControlLabel}
                />
              </RadioGroup>
            }
            name="reportType"
            control={control}
            onChange={([event]) => {
              setValue('departments', [])
              return event.target.value
            }}
          />
        </Box>

        {reportType === 'by_department' && (
          <Box className={classes.sectionBox}>
            <Typography className={classes.sectionLabel}>
              Selecione os departamentos:
            </Typography>
            <Controller
              as={
                <Autocomplete
                  multiple
                  loading={isLoadingDepartments}
                  options={departments}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, value) => option.id === value.id}
                  noOptionsText="Nenhum departamento encontrado"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder="Selecione os departamentos"
                    />
                  )}
                />
              }
              name="departments"
              control={control}
              defaultValue={[]}
              onChange={([, data]) => data}
            />
          </Box>
        )}

        <Box className={classes.sectionBox}>
          <Typography className={classes.sectionLabel}>
            Escolha o Formato do Relatório:
          </Typography>
          <Controller
            as={
              <RadioGroup row className={classes.formatRow}>
                <FormControlLabel
                  value="pdf"
                  control={<Radio color="primary" size="small" />}
                  label="PDF"
                  className={classes.formControlLabel}
                />
                <FormControlLabel
                  value="xlsx"
                  control={<Radio color="primary" size="small" />}
                  label="Excel"
                  className={classes.formControlLabel}
                />
              </RadioGroup>
            }
            name="format"
            control={control}
            onChange={([event]) => event.target.value}
          />
        </Box>

        <Box className={classes.sectionBox}>
          <Typography className={classes.sectionLabel}>
            E-mails para receber o relatório:
          </Typography>
          <Controller
            as={
              <TextField
                placeholder="email@empresa.com; outro@empresa.com"
                variant="outlined"
                size="small"
                fullWidth
                className={classes.emailField}
              />
            }
            name="emails"
            control={control}
          />
          <Typography className={classes.emailHint}>
            Separe múltiplos e-mails com ; ou ,
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          className={classes.cancelButton}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          className={classes.generateButton}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Gerar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GenerateReportDialog
